import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'roiner-training-v1'

/** Fecha local en formato YYYY-MM-DD (no UTC: a las 7am importa el día de aquí). */
export function toISODate(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function formatDate(iso) {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y.slice(2)}`
}

/** Días transcurridos entre dos fechas ISO. */
function daysBetween(isoA, isoB) {
  const a = new Date(`${isoA}T00:00:00`)
  const b = new Date(`${isoB}T00:00:00`)
  return Math.round((a - b) / 86400000)
}

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { version: 1, days: {} }
    const parsed = JSON.parse(raw)
    return parsed && parsed.days ? parsed : { version: 1, days: {} }
  } catch (e) {
    return { version: 1, days: {} }
  }
}

function write(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    // Modo privado de Safari o cuota llena: la sesión sigue funcionando en memoria.
  }
}

const emptyEntry = { done: false, sets: [] }

/**
 * Registro de entrenamiento persistido en localStorage.
 *
 * Estructura: days[DIA][fechaISO][ejercicioId] = { done, sets: [{ w, r }] }
 * Se guarda una sesión por día-de-la-semana y fecha, para poder comparar
 * contra la última vez que se hizo ese mismo ejercicio.
 */
export function useTrainingLog() {
  const [data, setData] = useState({ version: 1, days: {} })
  const [hydrated, setHydrated] = useState(false)
  const [today, setToday] = useState(() => toISODate())

  // Leemos después del primer render, no durante: así el primer pintado no
  // depende de localStorage (Safari privado, cuota llena) y `hydrated` le
  // dice a la UI cuándo los contadores ya son reales.
  useEffect(() => {
    setData(read())
    setToday(toISODate())
    setHydrated(true)
  }, [])

  const update = useCallback((mutate) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev))
      mutate(next)
      write(next)
      return next
    })
  }, [])

  const getSession = useCallback(
    (dayId, date = today) => data.days?.[dayId]?.[date] || {},
    [data, today]
  )

  const getEntry = useCallback(
    (dayId, exerciseId, date = today) => getSession(dayId, date)[exerciseId] || emptyEntry,
    [getSession, today]
  )

  const toggleDone = useCallback(
    (dayId, exerciseId) => {
      update((next) => {
        const day = (next.days[dayId] = next.days[dayId] || {})
        const session = (day[today] = day[today] || {})
        const entry = (session[exerciseId] = session[exerciseId] || { done: false, sets: [] })
        entry.done = !entry.done
      })
    },
    [update, today]
  )

  const setSetValue = useCallback(
    (dayId, exerciseId, index, field, value) => {
      update((next) => {
        const day = (next.days[dayId] = next.days[dayId] || {})
        const session = (day[today] = day[today] || {})
        const entry = (session[exerciseId] = session[exerciseId] || { done: false, sets: [] })
        while (entry.sets.length <= index) entry.sets.push({ w: '', r: '' })
        entry.sets[index] = { ...entry.sets[index], [field]: value }
      })
    },
    [update, today]
  )

  const resetDay = useCallback(
    (dayId) => {
      update((next) => {
        if (next.days[dayId]) delete next.days[dayId][today]
      })
    },
    [update, today]
  )

  /** Última sesión anterior a hoy en la que se registró carga en este ejercicio. */
  const lastLogged = useCallback(
    (dayId, exerciseId) => {
      const day = data.days?.[dayId]
      if (!day) return null
      const dates = Object.keys(day)
        .filter((d) => d < today)
        .sort()
        .reverse()
      for (const date of dates) {
        const sets = day[date]?.[exerciseId]?.sets || []
        const filled = sets.filter((s) => s && (s.w || s.r))
        if (filled.length) return { date, sets: filled, daysAgo: daysBetween(today, date) }
      }
      return null
    },
    [data, today]
  )

  /** Sesiones con al menos un ejercicio marcado en los últimos 7 días. */
  const recentSessions = useCallback(() => {
    const dates = new Set()
    Object.values(data.days || {}).forEach((day) => {
      Object.entries(day).forEach(([date, session]) => {
        const anyDone = Object.values(session).some((e) => e.done)
        if (anyDone && daysBetween(today, date) < 7 && daysBetween(today, date) >= 0) {
          dates.add(date)
        }
      })
    })
    return dates.size
  }, [data, today])

  return {
    hydrated,
    today,
    getEntry,
    toggleDone,
    setSetValue,
    resetDay,
    lastLogged,
    recentSessions,
  }
}
